import { ui } from '@procuros/datachecks';

import {
  COLUMNS_DATA_FORMATS_MAP,
  RowDataType,
  SheetExpectedData,
  TemplateValidationErrorCodes,
  ValidationErrorDetail,
} from './helpers';

export const validator = ({
  expectedData,
  setSheetName,
  data,
}: {
  expectedData: SheetExpectedData;
  setSheetName: (sheetName: string) => void; //we don't like this, but we need to find the sheet name with the data
  data: Record<string, Array<Record<string, unknown>>>;
}):
  | { success: true }
  | {
      success: false;
      errorCode: TemplateValidationErrorCodes;
      columns: Array<string>;
      errorDetails?: Array<ValidationErrorDetail>;
    } => {
  let hasValidSheet = false;
  let lastError: TemplateValidationErrorCodes | null = null;
  let columns: Array<string> = [];

  for (const sheetName in data) {
    const sheetData = data[sheetName] || [];

    const hasValidHeaders = validHeaders(sheetData, expectedData.headers);
    if (!hasValidHeaders.success) {
      lastError = TemplateValidationErrorCodes.INVALID_HEADERS;
      columns = hasValidHeaders.missingHeaders;
      continue;
    }

    // Headers are valid, set the sheet name and stop checking other sheets
    setSheetName(sheetName);

    const validationResult = validateDataFormats(sheetData, expectedData.formats);

    if (!validationResult.success) {
      lastError = TemplateValidationErrorCodes.INVALID_DATA_FORMATS;
      columns = validationResult.invalidColumns;
      return {
        success: false,
        errorCode: lastError,
        columns,
        errorDetails: validationResult.errorDetails,
      };
    }

    hasValidSheet = true;
    break; // Exit the loop if a valid sheet is found
  }

  if (hasValidSheet) {
    return { success: true };
  }

  return { success: false, errorCode: lastError || TemplateValidationErrorCodes.INVALID_HEADERS, columns };
};

export const validHeaders = (
  data: Array<RowDataType>,
  expectedHeaders: Array<string>,
): { success: boolean; missingHeaders: Array<string> } => {
  // Handle empty array case
  if (!data || data.length === 0) {
    return { success: false, missingHeaders: expectedHeaders };
  }

  // Each row is a row data object, where the keys are the column headers
  const headers = Object.keys(data[0]);
  // Check if the headers are in the expected format
  if (!Array.isArray(headers) || headers.length === 0) {
    return { success: false, missingHeaders: expectedHeaders };
  }

  // Check if all expected headers are present
  // Assuming headers are in the first row of the first sheet
  const missingHeaders = expectedHeaders.filter((header) => !headers.includes(header));
  if (missingHeaders.length > 0) {
    return { success: false, missingHeaders };
  }
  return { success: true, missingHeaders: [] };
};

export const validateDataFormats = (
  data: Array<Record<string, unknown>>,
  expectedDataFormats: Record<string, ui.FieldTypes>,
): { success: boolean; invalidColumns: Array<string>; errorDetails: Array<ValidationErrorDetail> } => {
  // Handle empty array case
  if (!data || data.length === 0) {
    return { success: true, invalidColumns: [], errorDetails: [] };
  }

  const errorDetails: Array<ValidationErrorDetail> = [];
  const invalidColumnsSet = new Set<string>();

  // Single iteration through all data
  for (const header in expectedDataFormats) {
    const expectedFormat = expectedDataFormats[header];
    for (let rowIndex = 0; rowIndex < data.length; rowIndex++) {
      const row = data[rowIndex];
      const value = row[header];

      if (value === undefined || value === null) {
        continue;
      }

      if (!COLUMNS_DATA_FORMATS_MAP[expectedFormat](value)) {
        invalidColumnsSet.add(header);
        errorDetails.push({
          row: rowIndex + 1,
          column: header,
          value: String(value),
          expectedFormat,
        });
      }
    }
  }

  return {
    success: errorDetails.length === 0,
    invalidColumns: Array.from(invalidColumnsSet),
    errorDetails,
  };
};
